import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme, Box } from '@mui/material';
import { MonthlyRentData } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';

interface SimpleMonthlyRentChartProps {
  data: MonthlyRentData[];
  height?: number;
  onStartInvesting?: () => void;
}

export default function SimpleMonthlyRentChart({
  data,
  height = 350,
}: SimpleMonthlyRentChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const hasData = data.length > 0;

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const option = {
      backgroundColor: 'transparent',
      legend: {
        top: 10,
        left: 'center',
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
          fontWeight: 500,
        },
        itemGap: 20,
        data: [
          {
            name: 'Neto operativo estimado',
            icon: 'circle',
          },
          {
            name: 'Cashflow estimado',
            icon: 'rect',
          },
        ],
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: theme.palette.divider,
        borderWidth: 1,
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
        },
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const month = params[0]?.name || params[0]?.axisValueLabel || '';
          let tooltipContent = `<div style="font-weight: 600; margin-bottom: 8px;">${month}</div>`;

          params.forEach((param) => {
            const valueInEuros = typeof param.value === 'number' ? param.value : 0;
            // Convert back to cents for formatCurrency function
            const valueInCents = Math.round(valueInEuros * 100);
            const formattedValue = formatCurrency(valueInCents);

            tooltipContent += `
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${
                  param.color || theme.palette.primary.main
                }; border-radius: ${
                  param.seriesName === 'Neto operativo estimado' ? '50%' : '2px'
                }; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600; margin-left: 8px;">${formattedValue}</span>
              </div>
            `;
          });

          return tooltipContent;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '20%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.month),
        axisLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Importe (€)',
        axisLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          formatter: (value: number) => {
            if (value >= 1000) {
              return `€${(value / 1000).toFixed(0)}k`;
            }
            return `€${value.toFixed(0)}`;
          },
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: 'Neto operativo estimado',
          type: 'line',
          data: data.map((d) => d.estimatedNetOperatingIncome / 100), // Convert cents to euros for chart display
          smooth: true,
          lineStyle: {
            width: 3,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: hasData ? theme.palette.primary.main : theme.palette.grey[400] },
              { offset: 1, color: hasData ? theme.palette.primary.light : theme.palette.grey[300] },
            ]),
          },
          itemStyle: {
            color: hasData ? theme.palette.primary.main : theme.palette.grey[400],
            borderWidth: 2,
            borderColor: '#fff',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: hasData ? `${theme.palette.primary.main}20` : `${theme.palette.grey[400]}15`,
              },
              {
                offset: 1,
                color: hasData ? `${theme.palette.primary.main}05` : `${theme.palette.grey[400]}05`,
              },
            ]),
          },
          emphasis: {
            focus: 'series',
            scale: true,
          },
          symbol: 'circle',
          symbolSize: 6,
        },
        {
          name: 'Cashflow estimado',
          type: 'line',
          data: data.map((d) => d.estimatedCashflow / 100), // Convert cents to euros for chart display
          smooth: true,
          lineStyle: {
            width: 2.5,
            type: 'dashed',
            dashArray: [8, 4],
            color: hasData ? theme.palette.secondary.main : theme.palette.grey[500],
          },
          itemStyle: {
            color: hasData ? theme.palette.secondary.main : theme.palette.grey[500],
            borderWidth: 2,
            borderColor: '#fff',
          },
          emphasis: {
            focus: 'series',
            scale: true,
          },
          symbol: 'rect',
          symbolSize: 6,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data, theme, hasData]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: `${height}px` }}>
      <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />
    </Box>
  );
}
